import { Controller } from "@hotwired/stimulus"

// Errors that end the dictation: the next attempt would fail the same way. The others ("no-speech" after a silence,
// "aborted") only end the browser's session, which restarts while the user has not clicked stop.
const FATAL_ERRORS = ["not-allowed", "service-not-allowed", "audio-capture", "network", "language-not-supported"]

// Voice dictation into a text box, with the browser's speech recognition in French (Web Speech API: Chrome, Edge,
// Safari). The button starts and stops it; the words appear as they are recognised, after the text already typed.
// Chrome ends a session after a few seconds of silence: it restarts until the button is clicked again. A blocked
// microphone or an unreachable service stops it with a message. The button stays hidden where the browser cannot
// dictate (Firefox).
export default class extends Controller {
  static targets = ["input", "button", "label", "error"]
  static classes = ["recording"]
  static values = {
    lang: { type: String, default: "fr-FR" },
    startLabel: String,
    stopLabel: String,
    errorMessages: Object
  }

  connect () {
    const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition
    if (!Recognition) return

    this.recognition = new Recognition()
    this.recognition.lang = this.langValue
    this.recognition.continuous = true
    this.recognition.interimResults = true
    this.recognition.onresult = (event) => this.write(event)
    this.recognition.onerror = (event) => this.fail(event.error)
    this.recognition.onend = () => this.ended()
    this.buttonTarget.hidden = false
  }

  disconnect () {
    this.listening = false
    this.recognition?.abort()
  }

  toggle (event) {
    event.preventDefault()
    if (this.listening) {
      this.listening = false
      this.recognition.stop()
    } else {
      this.showError(null)
      this.listening = true
      this.listen()
      this.render()
    }
  }

  // A new browser session writes after what the box holds now: typed text and earlier dictation.
  listen () {
    this.typedText = this.inputTarget.value.trim()
    this.dictatedText = ""
    this.recognition.start()
  }

  // Final results add up; the interim one (still being recognised) is shown after them, then replaced.
  write (event) {
    let interimText = ""
    for (let index = event.resultIndex; index < event.results.length; index++) {
      const result = event.results[index]
      if (result.isFinal) {
        this.dictatedText += ` ${result[0].transcript}`
      } else {
        interimText += ` ${result[0].transcript}`
      }
    }

    this.inputTarget.value = `${this.typedText} ${this.dictatedText} ${interimText}`.replace(/\s+/g, " ").trim()
    this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }))
  }

  fail (error) {
    if (!FATAL_ERRORS.includes(error)) return

    this.listening = false
    this.showError(this.errorMessagesValue[error] || this.errorMessagesValue.default)
  }

  ended () {
    if (this.listening) {
      try {
        this.listen()
        return
      } catch {
        this.listening = false
      }
    }

    this.render()
    this.inputTarget.focus()
  }

  render () {
    const label = this.listening ? this.stopLabelValue : this.startLabelValue
    this.buttonTarget.classList.toggle(this.recordingClass, Boolean(this.listening))
    this.buttonTarget.setAttribute("aria-pressed", String(Boolean(this.listening)))
    this.buttonTarget.title = label
    this.labelTarget.textContent = label
  }

  showError (message) {
    if (!this.hasErrorTarget) return

    this.errorTarget.textContent = message || ""
    this.errorTarget.hidden = !message
  }
}
